import { Button, DialogActions, DialogTitle, Icon } from "@material-ui/core";
import React, { Component } from "react";
import styled from "styled-components";

import { PREDEFINED_PALETTES } from "common/constants";
import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import { app } from "js/namespaces";
import { _ } from "js/vendor";

import { BeautifulDialog, DialogContent, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ds } from "js/core/models/dataService";

import WorkspaceController from "js/controllers/WorkspaceController";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { ThemeThumbnailList } from "js/editor/ThemeEditor/components/ThemeThumbnailList";
import { BlueButton } from "js/react/components/UiComponents";
import { t } from "js/i18n";
import themeManager from "js/core/themeManager";
import { Theme } from "js/core/models/theme";
import { SharedTheme } from "js/core/models/sharedTheme";

import "css/style-guide.scss";

const Subtitle = styled.div`
    margin-top: 10px;

    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 125%;
    letter-spacing: 0.5px;
    color: #222222;
`;

class ChooseThemeDialogInner extends Component {
    static defaultProps = {
        title: "Select Theme",
        subtitle: null,
        selectButtonLabel: "Select Theme",
        preventClose: false,
        hideUserThemes: false,
        hideBuiltInThemes: false,
        onlyTeamThemes: false,
        showCancelButton: true,
        closeDialogOnThemeChoose: true,
        callback: null,
        selectedThemeId: null,
    }

    state = {
        selectedTheme: null,
    }

    componentDidMount() {
        const { hideBuiltInThemes, hideUserThemes, onlyTeamThemes, pptThemeModel, selectedThemeId } = this.props;

        (async () => {
            const pptTheme = pptThemeModel ? new Theme(
                pptThemeModel,
                {
                    disconnected: true,
                    autoSync: false
                }
            ) : null;
            this.setState({ pptTheme });

            const selectedThemeResource = selectedThemeId ? await themeManager.getThemeResource(selectedThemeId) : null;
            const selectedBuiltInTheme = (selectedThemeId && !hideBuiltInThemes) ? ds.builtInThemes.models.find(theme => theme.id === selectedThemeId) : null;

            if (selectedBuiltInTheme) {
                this.handleSelectTheme(selectedBuiltInTheme);
                return;
            }

            if (selectedThemeResource && selectedThemeResource.metadata.isPublished) {
                if (selectedThemeResource.metadata.isShared) {
                    if (this.canUseSharedThemes()) {
                        const sharedTheme = await themeManager.getSharedTheme(selectedThemeId, true);
                        this.handleSelectTheme(sharedTheme);
                        return;
                    }
                } else if (!hideUserThemes) {
                    const userTheme = await themeManager.getUserTheme(selectedThemeId, true);
                    this.handleSelectTheme(userTheme);
                    return;
                }
            }

            // We only want to select the default theme
            if (this.canUseSharedThemes()) {
                const defaultTheme = await themeManager.getDefaultSharedTheme(true);
                if (defaultTheme) {
                    this.handleSelectTheme(defaultTheme);
                    return;
                }
            }

            if (onlyTeamThemes) {
                return;
            }

            if (!hideUserThemes) {
                const userTheme = await themeManager.getNewestUserTheme(true);
                if (userTheme) {
                    this.handleSelectTheme(userTheme);
                    return;
                }
            }

            if (!hideBuiltInThemes) {
                this.handleSelectTheme(ds.builtInThemes.models.at(0));
                return;
            }

            if (pptTheme) {
                this.handleChooseTheme(pptTheme);
            }
        })();
    }

    canUseSharedThemes = () => {
        return !!WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES]?.[PermissionActionType.USE];
    }

    isSharedTheme = theme => {
        return theme instanceof SharedTheme || theme?.root === "sharedThemes";
    }

    canChooseTheme = theme => {
        return !!theme && (!this.isSharedTheme(theme) || this.canUseSharedThemes());
    }

    handleSelectTheme = selectedTheme => {
        if (this.isSharedTheme(selectedTheme) && !this.canUseSharedThemes()) {
            return;
        }

        this.setState({ selectedTheme });
    }

    handleChooseTheme = async theme => {
        if (!this.canChooseTheme(theme)) {
            return;
        }

        theme = new Theme(_.cloneDeep(theme.attributes));

        this.handleSelectTheme();

        const { callback, closeDialog, closeDialogOnThemeChoose } = this.props;

        if (!theme.has("colors")) {
            const defaultThemePalette = _.find(PREDEFINED_PALETTES, { id: theme.get("palette_name") || "colorful" });
            theme.set("colors", defaultThemePalette.colors);
        }

        if (callback) {
            await callback(theme);
        }

        if (closeDialogOnThemeChoose) {
            closeDialog(theme);
        }
    }

    handleEditCurrentTheme = async () => {
        const { closeDialog } = this.props;

        closeDialog();

        await PresentationEditorController.showThemeEditor();
    }

    handleDeleteTheme = theme => {
        ShowConfirmationDialog({
            title: t("Are you sure you want to delete this theme?"),
            message: t("No existing presentations will be affected by this change but you won't be able to use this theme in any future presentations.")
        }).then(confirmed => {
            if (confirmed) {
                theme.collection.remove([theme]);
                theme.destroy();
            }
        });
    }

    render() {
        const {
            selectedTheme,
            pptTheme
        } = this.state;
        const {
            closeDialog,
            title,
            subtitle,
            selectButtonLabel,
            preventClose,
            onlyTeamThemes,
            showCancelButton,
            styles,
            showEditCurrentTheme = true,
            fullScreen = false
        } = this.props;

        // If we're creating a theme, we won't have a presentation
        const isTeamTheme = PresentationEditorController.presentation?.has("sharedThemeId");

        return (
            <BeautifulDialog
                maxWidth="lg"
                fullScreen={app.isMobileOrTablet || fullScreen}
                preventClose={preventClose}
                closeDialog={closeDialog}
            >
                <DialogTitle>
                    <FlexBox left>{t(title)}
                        <FlexSpacer />
                        {showEditCurrentTheme && (!isTeamTheme || WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage) &&
                            <Button onClick={this.handleEditCurrentTheme}>
                                <Icon color="primary">edit</Icon>
                                {t("Edit Current Theme...")}
                            </Button>
                        }
                    </FlexBox>
                    {subtitle && <Subtitle>{t(subtitle)}</Subtitle>}
                </DialogTitle>
                <DialogContent style={{ ...styles, background: "#f1f1f1", overflow: app.isMobileOrTablet ? "hidden" : null }}>
                    <Gap10 />
                    <ThemeThumbnailList
                        requireTeamTheme={onlyTeamThemes}
                        pptTheme={pptTheme}
                        selectedTheme={selectedTheme}
                        onClick={this.handleSelectTheme}
                        onDoubleClick={this.handleChooseTheme}
                    />
                </DialogContent>
                <DialogActions>
                    <FlexBox fillWidth>
                        <FlexSpacer />
                        {showCancelButton && <Button onClick={closeDialog}>Cancel</Button>}
                        <Gap20 />
                        <BlueButton
                            color="primary"
                            onClick={() => this.handleChooseTheme(selectedTheme)}
                            disabled={!this.canChooseTheme(selectedTheme)}
                        >
                            {t(selectButtonLabel)}
                        </BlueButton>
                    </FlexBox>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

export const ChooseThemeDialog = React.forwardRef((props, ref) => (
    <ChooseThemeDialogInner {...props} ref={ref} />
));
